import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Stack,
  Checkbox,
  LinearProgress,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  TablePagination,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  IoSearchOutline, 
  IoDownloadOutline, 
  IoCloudUploadOutline,
  IoFilterOutline,
} from 'react-icons/io5';
import { BiSelectMultiple } from 'react-icons/bi';

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '12px',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  '& .MuiTable-root': {
    minWidth: 650,
  },
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  '& .MuiTableCell-head': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
    fontWeight: 600,
    color: theme.palette.text.secondary,
    borderBottom: `2px solid ${theme.palette.divider}`,
  },
}));

const StyledTableRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== 'selected',
})(({ theme, selected }) => ({
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  ...(selected && {
    backgroundColor: theme.palette.success.light,
  }),
  '& .MuiTableCell-root': {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  padding: '6px',
  margin: '0 2px',
  borderRadius: '8px',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const AutobiaDataTable = ({
  data = [],
  columns = [],
  loading = false,
  selectable = false,
  searchable = true,
  exportable = false,
  importable = false,
  filterable = false,
  animated = true,
  variant = 'default',
  actions = [],
  onRowClick = null,
  onSelectionChange = () => {},
  onSearch = () => {},
  onExport = () => {},
  onImport = () => {},
  pagination = false,
  rowsPerPageOptions = [5, 10, 25, 50],
  emptyMessage = 'No data available',
  maxHeight = 600,
  ...props
}) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[1] || 10);

  // Filter data based on search
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    
    return data.filter((row) =>
      columns.some((column) => {
        const value = row[column.id];
        if (value == null) return false;
        return value.toString().toLowerCase().includes(searchTerm.toLowerCase());
      })
    );
  }, [data, searchTerm, columns]);

  // Paginated data
  const paginatedData = useMemo(() => {
    if (!pagination) return filteredData;
    const start = page * rowsPerPage;
    return filteredData.slice(start, start + rowsPerPage);
  }, [filteredData, page, rowsPerPage, pagination]);

  const handleSelectRow = (rowId) => {
    const newSelected = selectedRows.includes(rowId)
      ? selectedRows.filter(id => id !== rowId)
      : [...selectedRows, rowId];
    
    setSelectedRows(newSelected);
    onSelectionChange(newSelected);
  };

  const handleSelectAll = () => {
    const allIds = filteredData.map(row => row.id);
    const newSelected = selectedRows.length === allIds.length ? [] : allIds;
    setSelectedRows(newSelected);
    onSelectionChange(newSelected);
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPage(0); // Reset to first page on search
    onSearch(value);
  };

  const renderCellContent = (column, row) => {
    const value = row[column.id];

    if (column.render) {
      return column.render(value, row);
    }

    if (column.type === 'currency') {
      return (
        <Typography variant="body2" fontWeight={600}>
          {new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: column.currency || 'USD',
          }).format(value)}
        </Typography>
      );
    }

    if (column.type === 'chip') {
      return (
        <Chip
          label={value}
          size="small"
          color={column.chipColor?.(value) || 'default'}
          variant={column.chipVariant || 'filled'}
        />
      );
    }

    if (column.type === 'date') {
      return new Date(value).toLocaleDateString();
    }

    return (
      <Typography 
        variant="body2"
        fontWeight={column.fontWeight || 400}
        color={column.color?.(value) || 'inherit'}
      >
        {value}
      </Typography>
    );
  };

  const renderActions = (row) => {
    if (!actions.length) return null;

    return (
      <Stack direction="row" spacing={0.5}>
        {actions.map((action, index) => (
          <Tooltip key={index} title={action.tooltip || action.label}>
            <ActionButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                action.onClick(row);
              }}
              disabled={action.disabled?.(row)}
              color={action.color || 'default'}
            >
              {action.icon}
            </ActionButton>
          </Tooltip>
        ))}
      </Stack>
    );
  };

  const TableRowComponent = animated 
    ? motion(StyledTableRow)
    : StyledTableRow;

  return (
    <Stack spacing={2}>
      {/* Table Controls */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Stack direction="row" alignItems="center" gap={1}>
          {searchable && (
            <TextField
              placeholder="Search..."
              size="small"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IoSearchOutline />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 250 }}
            />
          )}
          {filterable && (
            <IconButton>
              <IoFilterOutline />
            </IconButton>
          )}
        </Stack>

        <Stack direction="row" alignItems="center" gap={1}>
          {selectedRows.length > 0 && selectable && (
            <Chip
              label={`${selectedRows.length} selected`}
              variant="outlined"
              onDelete={() => {
                setSelectedRows([]);
                onSelectionChange([]);
              }}
              deleteIcon={<BiSelectMultiple />}
            />
          )}
          {exportable && (
            <IconButton onClick={onExport}>
              <IoDownloadOutline />
            </IconButton>
          )}
          {importable && (
            <IconButton onClick={onImport}>
              <IoCloudUploadOutline />
            </IconButton>
          )}
        </Stack>
      </Stack>

      {/* Table */}
      <StyledTableContainer sx={{ maxHeight }}>
        <Table stickyHeader>
          <StyledTableHead>
            <TableRow>
              {selectable && (
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedRows.length > 0 && selectedRows.length < filteredData.length}
                    checked={selectedRows.length === filteredData.length && filteredData.length > 0}
                    onChange={handleSelectAll}
                  />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || 'left'}
                  style={{ minWidth: column.minWidth }}
                >
                  {column.label}
                </TableCell>
              ))}
              {actions.length > 0 && (
                <TableCell align="center">Actions</TableCell>
              )}
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={columns.length + (selectable ? 1 : 0) + (actions.length > 0 ? 1 : 0)}>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}
            
            {!loading && paginatedData.length === 0 && (
              <TableRow>
                <TableCell 
                  colSpan={columns.length + (selectable ? 1 : 0) + (actions.length > 0 ? 1 : 0)}
                  align="center"
                  sx={{ py: 4 }}
                >
                  <Typography variant="body1" color="text.secondary">
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            <AnimatePresence>
              {paginatedData.map((row, index) => (
                <TableRowComponent
                  key={row.id}
                  selected={selectedRows.includes(row.id)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
                  {...(animated && {
                    initial: { opacity: 0, y: 10 },
                    animate: { opacity: 1, y: 0 },
                    exit: { opacity: 0, y: -10 },
                    transition: { duration: 0.2, delay: index * 0.05 },
                  })}
                >
                  {selectable && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedRows.includes(row.id)}
                        onChange={() => handleSelectRow(row.id)}
                      />
                    </TableCell>
                  )}
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align || 'left'}
                    >
                      {renderCellContent(column, row)}
                    </TableCell>
                  ))}
                  {actions.length > 0 && (
                    <TableCell align="center">
                      {renderActions(row)}
                    </TableCell>
                  )}
                </TableRowComponent>
              ))}
            </AnimatePresence>
          </TableBody>
        </Table>
      </StyledTableContainer>

      {/* Pagination */}
      {pagination && (
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      )}
    </Stack>
  );
};

export default AutobiaDataTable;